import React, { useState } from 'react';

const GrammarUnit12Content = ({ unitData, userPreferences, onMarkComplete, userProgress }) => {
  const [currentSection, setCurrentSection] = useState('overview');

  // Safety check for unit data
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/30 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">No Content Available</h2>
            <p className="text-gray-300">Content for Unit 12 is not available.</p>
          </div>
        </div>
      </div>
    );
  }

  const { content } = unitData;

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">介系詞概覽</h3>
        <p className="text-gray-300 mb-6">{content.overview.description}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {content.overview.types.map((type, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{type.type}</h4>
              <p className="text-yellow-300 font-mono mb-2">{type.examples}</p>
              <p className="text-gray-300">{type.function}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTimePrepositions = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">時間介系詞</h3>
        <p className="text-gray-300 mb-6">{content.time_prepositions.description}</p>
        
        <div className="space-y-4">
          {content.time_prepositions.prepositions.map((prep, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{prep.preposition}</h4>
              <p className="text-yellow-300 mb-2">{prep.usage}</p>
              <div className="space-y-1">
                {prep.examples.map((example, idx) => (
                  <p key={idx} className="text-blue-300 text-sm">{example}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPlacePrepositions = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">地點介系詞</h3>
        <p className="text-gray-300 mb-6">{content.place_prepositions.description}</p>
        
        <div className="space-y-4">
          {content.place_prepositions.prepositions.map((prep, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{prep.preposition}</h4>
              <p className="text-yellow-300 mb-2">{prep.usage}</p>
              <div className="space-y-1">
                {prep.examples.map((example, idx) => (
                  <p key={idx} className="text-blue-300 text-sm">{example}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDirectionPrepositions = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">方向介系詞</h3>
        <p className="text-gray-300 mb-6">{content.direction_prepositions.description}</p>
        
        <div className="space-y-4">
          {content.direction_prepositions.prepositions.map((prep, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{prep.preposition}</h4>
              <p className="text-yellow-300 mb-2">{prep.usage}</p>
              <div className="space-y-1">
                {prep.examples.map((example, idx) => (
                  <p key={idx} className="text-blue-300 text-sm">{example}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMannerPrepositions = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">方式介系詞</h3>
        <p className="text-gray-300 mb-6">{content.manner_prepositions.description}</p>
        
        <div className="space-y-4">
          {content.manner_prepositions.prepositions.map((prep, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{prep.preposition}</h4>
              <p className="text-yellow-300 mb-2">{prep.usage}</p>
              <div className="space-y-1">
                {prep.examples.map((example, idx) => (
                  <p key={idx} className="text-blue-300 text-sm">{example}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCausePrepositions = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">原因介系詞</h3>
        <p className="text-gray-300 mb-6">{content.cause_prepositions.description}</p>
        
        <div className="space-y-4">
          {content.cause_prepositions.prepositions.map((prep, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{prep.preposition}</h4>
              <p className="text-yellow-300 mb-2">{prep.usage}</p>
              <div className="space-y-1">
                {prep.examples.map((example, idx) => (
                  <p key={idx} className="text-blue-300 text-sm">{example}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderExercises = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">練習題</h3>
        
        <div className="space-y-6">
          {content.exercises.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{section.name}</h4>
              
              <div className="space-y-4">
                {section.questions.map((question, questionIndex) => (
                  <div key={questionIndex} className="bg-gray-600/30 p-3 rounded">
                    <p className="text-gray-300 mb-3">{question.question}</p>
                    <div className="space-y-2">
                      {Object.entries(question.options).map(([key, option]) => (
                        <div key={key} className="flex items-center">
                          <span className="text-yellow-300 font-mono mr-3">{key}.</span>
                          <span className="text-gray-300">{option}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="bg-gray-800/30 rounded-xl p-4 mb-6">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCurrentSection('overview')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'overview'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          概覽
        </button>
        <button
          onClick={() => setCurrentSection('time')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'time'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          時間
        </button>
        <button
          onClick={() => setCurrentSection('place')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'place'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          地點
        </button>
        <button
          onClick={() => setCurrentSection('direction')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'direction'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          方向
        </button>
        <button
          onClick={() => setCurrentSection('manner')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'manner'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          方式
        </button>
        <button
          onClick={() => setCurrentSection('cause')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'cause'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          原因
        </button>
        <button
          onClick={() => setCurrentSection('exercises')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'exercises'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          練習題
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2 text-blue-300">
            {unitData.title[userPreferences?.language || 'traditional']}
          </h1>
          <p className="text-gray-300 mb-4">
            {unitData.subtitle[userPreferences?.language || 'traditional']}
          </p>
          <p className="text-gray-400">
            {unitData.description[userPreferences?.language || 'traditional']}
          </p>
        </div>

        {/* Navigation */}
        {renderNavigation()}

        {/* Content */}
        {currentSection === 'overview' && renderOverview()}
        {currentSection === 'time' && renderTimePrepositions()}
        {currentSection === 'place' && renderPlacePrepositions()}
        {currentSection === 'direction' && renderDirectionPrepositions()}
        {currentSection === 'manner' && renderMannerPrepositions()}
        {currentSection === 'cause' && renderCausePrepositions()}
        {currentSection === 'exercises' && renderExercises()}

        {/* Complete Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => onMarkComplete && onMarkComplete(unitData.id)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            標記為完成
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrammarUnit12Content;
